// Basic frontend validation utilities for signup/login forms
// These do not guarantee uniqueness without backend support; they only perform format checks.

export interface FieldErrors {
  username?: string;
  email?: string;
  phone?: string;
  displayName?: string;
  bio?: string;
  oldPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const usernameRegex = /^[a-zA-Z0-9_.-]{3,}$/; // allow letters, numbers, underscore, dot, dash

export function validateSignupFields(fields: {
  username: string;
  email: string;
  phone: string; // digits only without country code
  displayName: string;
  bio: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!fields.displayName.trim()) {
    errors.displayName = 'Display name is required';
  }
  if (!fields.bio.trim()) {
    errors.bio = 'Bio is required';
  }
  if (!usernameRegex.test(fields.username)) {
    errors.username = 'Username must be at least 3 characters (letters, numbers, . _ -)';
  }
  if (!emailRegex.test(fields.email)) {
    errors.email = 'Invalid email format';
  }
  const digitsOnly = fields.phone.replace(/[^0-9]/g, '');
  if (digitsOnly.length !== 10) {
    errors.phone = 'Phone must be exactly 10 digits';
  }
  return errors;
}

export function mergeAvailabilityErrors(existing: FieldErrors, availability: {
  usernameExists?: boolean;
  emailExists?: boolean;
  phoneExists?: boolean;
}): FieldErrors {
  const updated = { ...existing };
  if (availability.usernameExists) {
    updated.username = 'Username already taken';
  }
  if (availability.emailExists) {
    updated.email = 'Email already registered';
  }
  if (availability.phoneExists) {
    updated.phone = 'Phone already registered';
  }
  return updated;
}

export function hasErrors(errors: FieldErrors): boolean {
  return Object.values(errors).some(Boolean);
}

// Password complexity: min 8, at least 3 of 4 categories
export function validatePasswordComplexity(pw: string): string | undefined {
  if (pw.length < 8) return 'Password must be at least 8 characters';
  const categories = [/[A-Z]/, /[a-z]/, /\d/, /[^A-Za-z0-9]/];
  const met = categories.filter(r => r.test(pw)).length;
  if (met < 3) return 'Include 3 of: uppercase, lowercase, digit, symbol';
  return undefined;
}

export function validateForceChangePassword(fields: { oldPassword: string; newPassword: string; confirmPassword: string; }): FieldErrors {
  const errors: FieldErrors = {};
  if (!fields.oldPassword) errors.oldPassword = 'Current password required';
  const complexity = validatePasswordComplexity(fields.newPassword);
  if (complexity) errors.newPassword = complexity;
  if (fields.newPassword !== fields.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }
  return errors;
}
